import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CaretDown, Brain } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chain = ({ chain }) => (
  <div className="mt-2 space-y-1.5">
    {chain.map((s, i) => (
      <p key={i} className="text-xs text-slate-500 leading-relaxed"><span className="font-semibold text-slate-700">{i + 1}. {s.step}:</span> {s.detail}</p>
    ))}
  </div>
);

const DecisionCard = ({ id, d }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`decision-${id}`} className="bg-white border border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full p-6 text-left flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <p className="overline-label text-slate-400">{d.question}</p>
          <p className="font-heading font-bold text-base tracking-tight mt-1.5 leading-snug">{d.headline}</p>
        </div>
        <CaretDown size={16} className={`text-slate-400 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 space-y-4 border-t border-slate-100 pt-4">
          {d.recommendations.map((r, i) => (
            <div key={i} className="border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900">{r.recommendation || r.country || `#${i + 1}`}
                {r.confidence && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-slate-900 text-white">{r.confidence} confidence</span>}
              </p>
              <Chain chain={r.reasoning_chain} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DecisionsPage() {
  const { productId } = useProduct();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    setData(null);
    setAnswer(null);
    axios.get(`${API}/decision-engine/${productId}`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true);
    try {
      const r = await axios.post(`${API}/decision-engine/${productId}/ask`, { question: q });
      setAnswer(r.data);
    } catch (e) { console.error(e); }
    setAsking(false);
  };

  return (
    <PageShell pageId="decisions" overline="AI Decision Engine · The Brain" title="Decision Intelligence">
      <div data-testid="ask-panel" className="bg-slate-900 text-white p-6">
        <p className="overline-label text-slate-400 flex items-center gap-2"><Brain size={14} /> Ask the Decision Engine</p>
        <div className="flex gap-3 mt-3">
          <Input data-testid="ask-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder='e.g. "Which countries can share evidence?" or "What evidence should we generate first?"'
            className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" />
          <Button data-testid="ask-submit" onClick={ask} disabled={asking} className="bg-white text-slate-900 hover:bg-slate-200 rounded-none">
            {asking ? "Reasoning…" : "Ask"}
          </Button>
        </div>
        {answer && (
          <div data-testid="ask-answer" className="mt-4 border-t border-white/10 pt-4">
            {answer.matched_intent ? (
              <>
                <p className="text-xs text-slate-400">Matched intent: <span className="font-mono-data">{answer.matched_intent}</span></p>
                <p className="text-sm font-semibold mt-1.5">{answer.answer.headline}</p>
                {answer.answer.recommendations.slice(0, 3).map((r, i) => (
                  <p key={i} className="text-xs text-slate-300 mt-1.5">• {r.recommendation || r.country}: {r.reasoning_chain[0].detail}</p>
                ))}
              </>
            ) : (
              <p className="text-sm text-slate-300">{answer.message}</p>
            )}
          </div>
        )}
      </div>

      {!data ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Reasoning…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {Object.entries(data.decisions).map(([k, d]) => <DecisionCard key={k} id={k} d={d} />)}
        </div>
      )}
    </PageShell>
  );
}
